import React, { ReactNode } from "react"
import styled from "styled-components"
import { AnimatePresence, motion } from "framer-motion"
import { Paper } from "@mui/material"
import { theme } from "../../../theme"
import { ButtonArea } from "../../atoms/button"
import { Individual } from "../../common"
import { Avatar, AvatarSize } from "../../atoms/avatar"
import { formatFullName } from "../../formatters"
import { TextRegular } from "../../atoms/text"

export type ProfileMenuItemProps = {
  link: string
  label: string
  icon?: ReactNode
}

export type MenuProps = {
  items: Array<ProfileMenuItemProps>
  individual: Individual
}

export const Menu = (props: MenuProps) => {
  const { items, individual } = props
  return (
    <AnimatePresence>
      {
        <Root
          elevation={3}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ animate: { duration: 0.5 }, exit: { duration: 0.2 } }}
        >
          <ProfileSummary>
            <Avatar individual={individual} size={AvatarSize.medium} />
            <Info>
              <Name noWrap={true}>{formatFullName(individual.firstName, individual.lastName, individual.title)}</Name>
              <Organization noWrap={true}>{individual.organization}</Organization>
            </Info>
          </ProfileSummary>
          {items.map((item: ProfileMenuItemProps) => (
            <Item href={item.link}>
              <Icon>{item.icon}</Icon>
              <TextMarginLeft>{item.label}</TextMarginLeft>
            </Item>
          ))}
        </Root>
      }
    </AnimatePresence>
  )
}

const Root = styled(motion(Paper))`
  display: flex;
  flex-direction: column;
  min-width: 260px;
  color: ${theme.color.black1};
`

const Item = styled(ButtonArea)`
  width: 100%;
  height: ${theme.buttonSize.l};
  display: flex;
  align-items: center;
  border-top: 1px solid ${theme.color.white1};
  font-size: ${theme.fontSize.m};
  padding: 0 ${theme.spacing.l};

  &:hover {
    background: ${theme.color.blue9};
  }
`

const Icon = styled.div`
  margin-top: 4px;

  svg {
    width: ${theme.iconSize.l};
    height: ${theme.iconSize.l};
  }
`

const TextMarginLeft = styled.span`
  margin-left: ${theme.spacing.l};
`

const ProfileSummary = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  color: black;
`

const Info = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  ${theme.mui.breakpoints.up("sm")} {
    display: block;
  }
  text-align: center;
  margin-top: ${theme.spacing.m};
`

const Name = styled(TextRegular)`
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: ${theme.spacing.lx};
  color: #161819;
`
const Organization = styled(TextRegular)`
  font-style: normal;
  font-weight: 400;
  font-size: ${theme.fontSize.s};
  line-height: ${theme.lineHeight.s};
  color: #686b6b;
`
